"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Moon, Play, Power, Sun, Trash2 } from "lucide-react";
import { Terminal, ITheme } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { ConfirmationDialog } from "@/components/Dialogs/ConfirmationDialog";

type ConnState = "idle" | "connecting" | "connected" | "closed" | "error";
type ColorScheme = "light" | "dark";

const COLOR_SCHEME_STORAGE_KEY = "sandbox-explorer.colorScheme";

// Light theme — neutrals matched to the noxus light palette so the
// terminal blends with the rest of the app surfaces.
const LIGHT_THEME: ITheme = {
  background: "#ffffff",
  foreground: "#1e1e1e",
  cursor: "#1e1e1e",
  cursorAccent: "#ffffff",
  selectionBackground: "#d8e9f7",
  black: "#1e1e1e",
  red: "#d92d20",
  green: "#15803d",
  yellow: "#a16207",
  blue: "#1d4ed8",
  magenta: "#7e22ce",
  cyan: "#0e7490",
  white: "#e5e7eb",
  brightBlack: "#525252",
  brightRed: "#ef4444",
  brightGreen: "#22c55e",
  brightYellow: "#ca8a04",
  brightBlue: "#3b82f6",
  brightMagenta: "#a855f7",
  brightCyan: "#06b6d4",
  brightWhite: "#fafafa",
};

// Dark theme — high-contrast neutrals + tailwind ANSI 16 colours.
const DARK_THEME: ITheme = {
  background: "#0a0a0a",
  foreground: "#e5e5e5",
  cursor: "#e5e5e5",
  cursorAccent: "#0a0a0a",
  selectionBackground: "#404040",
  black: "#1a1a1a",
  red: "#ef4444",
  green: "#22c55e",
  yellow: "#eab308",
  blue: "#3b82f6",
  magenta: "#a855f7",
  cyan: "#06b6d4",
  white: "#e5e5e5",
  brightBlack: "#525252",
  brightRed: "#f87171",
  brightGreen: "#4ade80",
  brightYellow: "#facc15",
  brightBlue: "#60a5fa",
  brightMagenta: "#c084fc",
  brightCyan: "#22d3ee",
  brightWhite: "#fafafa",
};

const THEMES: Record<ColorScheme, ITheme> = {
  light: LIGHT_THEME,
  dark: DARK_THEME,
};

const loadInitialColorScheme = (): ColorScheme => {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage access can fail in private mode / sandboxed iframes
  }
  return "light";
};

const STATUS_BADGE: Record<
  ConnState,
  {
    label: string;
    variant:
      | "default"
      | "secondary"
      | "destructive"
      | "warning"
      | "success"
      | "outline";
  }
> = {
  idle: { label: "Disconnected", variant: "outline" },
  connecting: { label: "Connecting…", variant: "warning" },
  connected: { label: "Connected", variant: "success" },
  closed: { label: "Closed", variant: "outline" },
  error: { label: "Error", variant: "destructive" },
};

/**
 * SandboxExplorer
 * --------------
 * xterm.js terminal attached to a gVisor sandbox via one of the backend's
 * interactive-shell websockets (assistant or plugin), addressed by
 * `shellPath` — the backend route after `/api/backend`. When `resetPath`
 * is provided a Reset action POSTs it to destroy and re-provision the
 * sandbox; without it the Reset button is hidden (plugin sandboxes are
 * reset through the plugin lifecycle, not from the terminal).
 *
 * Wire protocol (matches agentsandbox manager's /ws/{sid}):
 *   client → server: {"type":"stdin","data":"ls\\n"}
 *                    {"type":"resize","rows":24,"cols":80}
 *   server → client: {"type":"connected","message":"..."}
 *                    {"type":"stdout","data":"..."}     # merged stdout+stderr
 *                    {"type":"error","message":"..."}
 */
export const SandboxExplorer = ({
  shellPath,
  resetPath,
}: {
  shellPath: string;
  resetPath?: string;
}) => {
  const [state, setState] = useState<ConnState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [colorScheme, setColorScheme] = useState<ColorScheme>(
    loadInitialColorScheme
  );
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);

  const writeToTerm = useCallback((data: string) => {
    termRef.current?.write(data);
  }, []);

  // Best-effort fit. xterm's FitAddon throws if the container has zero
  // dimensions (which happens when the component is mounted inside a
  // BaseDialog that's still animating in). We swallow those and retry
  // a few times — by the third frame the dialog has settled.
  const refit = useCallback(() => {
    const fit = fitAddonRef.current;
    if (!fit) return;
    try {
      fit.fit();
    } catch {
      // container not laid out yet
    }
  }, []);

  // Initialise the xterm Terminal once the container element is mounted.
  useEffect(() => {
    if (!containerRef.current || termRef.current) return;
    // xterm renders into a <canvas>; the canvas API does NOT resolve
    // CSS custom properties, so passing `var(--font-mono)` here would
    // fall back to whatever the browser's default canvas font is
    // (often a wide proportional font, which is what made the
    // characters look spaced out). Read the platform's actual
    // monospace stack off the document and pass it as a literal.
    const monoStack =
      (typeof window !== "undefined"
        ? getComputedStyle(document.documentElement)
            .getPropertyValue("--font-mono")
            .trim()
        : "") ||
      '"Geist Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace';

    const term = new Terminal({
      convertEol: false,
      cursorBlink: true,
      cursorStyle: "block",
      fontFamily: monoStack,
      fontSize: 12,
      lineHeight: 1.2,
      scrollback: 5000,
      theme: THEMES[colorScheme],
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    termRef.current = term;
    fitAddonRef.current = fit;

    // Force Ctrl-C / Ctrl-D / Ctrl-Z etc. through onData even when
    // the browser would normally intercept them (e.g. Ctrl-C when
    // xterm has a selection copies to clipboard and never fires
    // onData). We explicitly write the control byte to the websocket
    // and tell xterm not to process the key further.
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== "keydown") return true;
      const ws = wsRef.current;
      const connected = !!ws && ws.readyState === WebSocket.OPEN;

      // Cmd-K (Mac) opens the app command palette at the window level, but it
      // has no terminal byte — while a live shell is focused it belongs to
      // the terminal, so swallow it (stopPropagation keeps the palette from
      // popping over the shell) and send nothing. Ctrl-K is handled below as
      // a normal control byte (^K, kill-line). Left alone when disconnected so
      // the global shortcut still works.
      if (connected && e.metaKey && !e.ctrlKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      const ctrl = e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
      if (!ctrl) return true;
      // Map Ctrl-<letter> to the corresponding control byte.
      const code = e.key.length === 1 ? e.key.toUpperCase().charCodeAt(0) : 0;
      if (code < 0x40 || code > 0x5f) return true;
      if (connected && ws) {
        ws.send(
          JSON.stringify({
            type: "stdin",
            data: String.fromCharCode(code - 0x40),
          })
        );
        // The live shell just consumed this control key, so the app must not
        // also act on it. Without stopPropagation the event still bubbles to
        // the window-level Ctrl/Cmd-K command-palette listener, which would
        // pop the palette over the shell — Ctrl-K should send ^K, not open
        // quick-open. Left alone when disconnected (no byte sent).
        e.stopPropagation();
      }
      e.preventDefault();
      return false;
    });

    // Re-fit on the next few animation frames so we catch the dialog
    // open transition without needing to know about it.
    refit();
    requestAnimationFrame(() => {
      refit();
      requestAnimationFrame(() => refit());
    });

    term.writeln("\x1b[2mSandbox shell: click Connect to attach.\x1b[0m");

    const onDataDisposable = term.onData((data) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "stdin", data }));
      }
    });

    const onResizeDisposable = term.onResize(({ cols, rows }) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", cols, rows }));
      }
    });

    return () => {
      onDataDisposable.dispose();
      onResizeDisposable.dispose();
      term.dispose();
      termRef.current = null;
      fitAddonRef.current = null;
    };
    // refit is stable (depends only on a ref) and we deliberately want
    // the terminal to be created exactly once on mount, not whenever
    // refit's identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the current colour scheme to the live terminal and remember
  // the choice in localStorage so the next mount picks it up.
  useEffect(() => {
    const term = termRef.current;
    if (term) {
      term.options.theme = THEMES[colorScheme];
    }
    try {
      window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, colorScheme);
    } catch {
      // localStorage may be unavailable
    }
  }, [colorScheme]);

  const toggleColorScheme = useCallback(() => {
    setColorScheme((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  // Refit the terminal whenever the container resizes. We track the
  // last container size we fitted to and bail out if nothing changed,
  // because xterm's `term.resize()` writes to the canvas which can
  // cause the ResizeObserver to fire again — without this guard the
  // component spins in a "fit → canvas mutate → observe → fit"
  // feedback loop.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let lastWidth = 0;
    let lastHeight = 0;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      // Sub-pixel jitter is normal — only refit on real size changes.
      if (
        Math.abs(rect.width - lastWidth) < 1 &&
        Math.abs(rect.height - lastHeight) < 1
      ) {
        return;
      }
      lastWidth = rect.width;
      lastHeight = rect.height;
      refit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [refit]);

  const connect = useCallback(async () => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    setState("connecting");
    setError(null);

    // Same-origin URL through the Next.js custom server (server.js).
    // The server's `upgrade` handler authenticates against the Auth0
    // session cookie that the browser already has, mints a fresh
    // backend access token, and forwards the WS to BACKEND_URL — so
    // the browser doesn't need to know about BACKEND_URL or carry an
    // access token in the URL.
    const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${wsScheme}://${window.location.host}/api/backend${shellPath}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setState("connected");
      writeToTerm("\x1b[2mConnected.\x1b[0m\r\n");
      // One more fit so the resize we send below uses the dialog's
      // actual dimensions, not the pre-open guess.
      refit();
      const term = termRef.current;
      if (term) {
        ws.send(
          JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })
        );
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "stdout" && typeof msg.data === "string") {
          writeToTerm(msg.data);
        } else if (msg.type === "connected") {
          writeToTerm(`\x1b[32m[${msg.message ?? "connected"}]\x1b[0m\r\n`);
        } else if (msg.type === "error") {
          writeToTerm(
            `\r\n\x1b[31m[error: ${msg.message ?? "unknown"}]\x1b[0m\r\n`
          );
          setError(String(msg.message ?? "unknown"));
        }
      } catch {
        writeToTerm(String(event.data));
      }
    };

    ws.onerror = (ev) => {
      console.error("[sandbox-ws] onerror", ev);
      setError("WebSocket error");
      setState("error");
    };

    ws.onclose = (event) => {
      setState("closed");
      const reason = event.reason ? `: ${event.reason}` : "";
      writeToTerm(
        `\r\n\x1b[2m[connection closed code=${event.code}${reason}]\x1b[0m\r\n`
      );
    };
  }, [shellPath, writeToTerm, refit]);

  const disconnect = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      ws.close();
    }
    wsRef.current = null;
  }, []);

  // Wipe the sandbox: close any active shell, ask the backend to destroy
  // the underlying container, then clear the terminal. The next Connect
  // re-provisions a fresh sandbox via SandboxService.get_or_create.
  const handleReset = useCallback(async () => {
    if (!resetPath) return;
    setResetConfirmOpen(false);
    setResetting(true);
    setError(null);
    // Drop the live shell first — the WS sits on top of the sandbox we're
    // about to destroy, and waiting for the upstream close propagates as
    // a confusing "connection closed" message right before the fresh
    // welcome line.
    disconnect();
    setState("idle");
    try {
      await axios.post(`/api/backend${resetPath}`);
      termRef.current?.reset();
      termRef.current?.writeln(
        "\x1b[2mSandbox wiped: click Connect for a fresh shell.\x1b[0m"
      );
      toast.success("Sandbox reset");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast.error(`Failed to reset sandbox: ${msg}`);
    } finally {
      setResetting(false);
    }
  }, [resetPath, disconnect]);

  // Tear down the websocket on unmount.
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const badge = useMemo(() => {
    const { label, variant } = STATUS_BADGE[state];
    return <Badge variant={variant}>{label}</Badge>;
  }, [state]);

  const terminalBackground = THEMES[colorScheme].background ?? "#ffffff";

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Slim toolbar — no inline title because the wrapping dialog/page
          should provide one. The wrapping dialog already supplies a
          border + background, so we don't repeat them here. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b bg-card px-2 py-2">
        <div className="flex items-center gap-2">
          {badge}
          {state === "connecting" && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="iconsm"
            variant="ghost"
            onClick={toggleColorScheme}
            title={
              colorScheme === "light"
                ? "Switch to dark terminal"
                : "Switch to light terminal"
            }
          >
            {colorScheme === "light" ? (
              <Moon className="h-3.5 w-3.5" />
            ) : (
              <Sun className="h-3.5 w-3.5" />
            )}
          </Button>
          {resetPath && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetting}
              title="Delete all files and recreate the sandbox"
            >
              {resetting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
              Reset
            </Button>
          )}
          {state === "connected" ? (
            <Button size="sm" variant="outline" onClick={disconnect}>
              <Power className="h-3 w-3" />
              Disconnect
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={connect}
              disabled={state === "connecting" || resetting}
            >
              <Play className="h-3 w-3" />
              Connect
            </Button>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        title="Reset sandbox?"
        description="This will permanently delete all files in the current sandbox and provision a brand-new one. This cannot be undone."
        confirmText="Reset sandbox"
        confirmVariant="destructive"
        onConfirm={handleReset}
      />

      {error && (
        <div className="shrink-0 border-b border-destructive/20 bg-destructive/10 px-3 py-1 text-xs text-destructive">
          {error}
        </div>
      )}

      {/* Outer wrapper sets the background + padding; the inner
          containerRef has NO padding so FitAddon's row calculation
          matches exactly what's visible. With padding on the fit
          target, xterm computes rows from the full height and the
          last row renders past the visible area. */}
      <div
        className="min-h-0 flex-1 overflow-hidden p-2"
        style={{ backgroundColor: terminalBackground }}
        onClick={() => termRef.current?.focus()}
      >
        <div ref={containerRef} className="h-full w-full" />
      </div>
    </div>
  );
};

export default SandboxExplorer;
